import { ErrorMessage } from '@hookform/error-message';
import { type FC, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { _notNil, _isNil, _isBlank, _isNotBlank } from '@/littledash';

import type { ApiID } from '@/model/Common.model';
import type { MetadataFieldWithValue } from '@/model/Metadata.model';
import type { StudyFormMetadataItem } from '@/model/StudyForm.model';

import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { RouteService } from '@/support/RouteService';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate.tsx';

import Link from '@/components/UI/Link';

import type { CreateStudyAction, CreateStudyState } from '../../Create.model';
import { BenchlingFolderLookup, type FolderOptionType } from './BenchlingFolderLookup';

interface BenchlingIntegrationProps {
  state: CreateStudyState;
  dispatch: (value: CreateStudyAction) => void;
  metaglossary: StudyFormMetadataItem;
  studyID?: `${number}` | null;
  studyApiId?: ApiID<'sdy'> | null;
  isSaving?: boolean;
}

const BenchlingIntegration: FC<BenchlingIntegrationProps> = ({
  state,
  dispatch,
  metaglossary,
  studyID = null,
  studyApiId = null,
  isSaving = false,
}) => {
  const {
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext();
  const { setStepReady } = useStudyCreateStep();

  const { response: studyInfoResponse, invoke: refetchStudyInfo } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/info',
    path: { studyApiId: studyApiId ?? ('' as ApiID<'sdy'>) },
    options: {
      invokeOnInit: _notNil(studyApiId),
      onError: { toast: false, throw: false, capture: true, slug: 'benchling-folder-name-load' },
    },
  });

  const apiFolderName = studyInfoResponse?.type === 'success' ? studyInfoResponse.body.benchling_folder_name : null;
  const apiFolderId = studyInfoResponse?.type === 'success' ? studyInfoResponse.body.benchling_folder_id : null;

  // A cloned draft carries the folder id in benchling_folder but no display name (the name is
  // only resolvable via Benchling). With no studyApiId to enrich against, resolve it by id here.
  const watchedFolder: FolderOptionType | null = useWatch({ name: 'benchling_folder' });
  const draftFolderId = watchedFolder?.value?.id ?? null;
  const draftFolderName = watchedFolder?.value?.name ?? watchedFolder?.label ?? null;
  const shouldResolveDraftFolder = _isNil(studyApiId) && _isNotBlank(draftFolderId) && _isBlank(draftFolderName);

  const { response: folderResponse, invoke: resolveDraftFolder } = useApiHook({
    endpoint: 'GET /api/v1/folders/{folderId}',
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: false, capture: true, slug: 'benchling-folder-by-id-load' },
    },
  });

  useEffect(() => {
    if (shouldResolveDraftFolder && _notNil(draftFolderId)) {
      void resolveDraftFolder({ path: { folderId: draftFolderId } });
    }
  }, [shouldResolveDraftFolder, draftFolderId]);

  const resolvedFolderName = folderResponse?.type === 'success' ? folderResponse.body.name : null;
  const draftApiFolderName = _isNotBlank(draftFolderName) ? draftFolderName : resolvedFolderName;

  const metaIndex = state?.study?.metadata?.findIndex(({ glossary_id }) => glossary_id === metaglossary.info.id) ?? -1;
  const wasUpdating = useRef(false);

  useEffect(() => {
    setStepReady(true);
  }, [setStepReady]);

  useEffect(() => {
    if (wasUpdating.current && !isSaving && _notNil(studyApiId)) {
      void refetchStudyInfo();
    }
    wasUpdating.current = isSaving;
  }, [isSaving, refetchStudyInfo, studyApiId]);

  const initialFolder = getValues(`metadata[${metaIndex}]`);

  const updateStudy = (folder: FolderOptionType, metaGlossaryItem: MetadataFieldWithValue) => {
    const updatedFormMetadata = [...state.study.metadata];
    let newMetaValue;
    if (_notNil(initialFolder?.value)) {
      newMetaValue = { ...initialFolder, value: folder.value?.id ?? '' };
      updatedFormMetadata[metaIndex] = newMetaValue;
      setValue(`metadata[${metaIndex}]`, newMetaValue);
    } else {
      newMetaValue = { ...metaGlossaryItem, value: folder.value?.id ?? '', glossary_id: metaGlossaryItem.id };
      const newIndex = updatedFormMetadata.length;
      updatedFormMetadata[newIndex] = newMetaValue;
      setValue(`metadata[${newIndex}]`, newMetaValue);
    }

    if (_isNil(studyID)) {
      dispatch({
        type: 'updateStudy',
        data: { ...state.study, benchling_folder: folder, metadata: updatedFormMetadata },
      });
    }
  };

  const currentFolderId = apiFolderId ?? initialFolder?.value;

  const folderLabel = _notNil(studyID) ? (
    <>
      {_isNotBlank(currentFolderId) && (
        <label className="mb3 flex items-baseline" htmlFor="benchling_folder">
          <span>Current Folder: {_isNotBlank(apiFolderName) ? apiFolderName : currentFolderId}</span>
          {_isNotBlank(apiFolderName) && <span className="f7 ml2 dark-gray">{currentFolderId}</span>}
        </label>
      )}
      <label htmlFor="benchling_folder">Select a folder</label>
    </>
  ) : (
    <>
      {_isNotBlank(draftFolderId) && (
        <div className="mb3 flex items-baseline">
          <span>Current Folder: {_isNotBlank(draftApiFolderName) ? draftApiFolderName : draftFolderId}</span>
          {_isNotBlank(draftApiFolderName) && <span className="f7 ml2 dark-gray">{draftFolderId}</span>}
        </div>
      )}
      <label htmlFor="benchling_folder">Select a folder</label>
    </>
  );

  return (
    <>
      <div className="mb4">
        <h3 className="fw5 f5 pb2">Link a Benchling folder</h3>
        <p className="f6 lh-copy ma0">
          Specify the location in Benchling where study data will be contained.{' '}
          <Link to={RouteService.help('linking_benchling_folder')} openTab className="blue link">
            Read more
          </Link>
        </p>
      </div>
      <div className="ui-card mw7" style={{ minHeight: '150px' }}>
        <div className="pa4">
          <div>
            <div className="mb3">
              {folderLabel}
              <BenchlingFolderLookup updateStudy={updateStudy} metaGlossary={metaglossary} />
              <ErrorMessage
                errors={errors}
                name="benchling_folder"
                render={({ message }) => <p className="f6 red db pt2">{message}</p>}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BenchlingIntegration;
